/* Kids Fun layer: reveal on scroll, typing names, parallax stickers, snow, countdown, video, confetti. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const once = (el, key) => {
    if (el.dataset[key]) return false;
    el.dataset[key] = '1';
    return true;
  };

  /* Reveal on scroll (everything visible straight away in the theme editor) */
  const revealer = 'IntersectionObserver' in window && !designMode
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            revealer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })
    : null;
  function initReveal(root) {
    root.querySelectorAll('.kf-reveal:not(.is-in)').forEach((el) => (revealer ? revealer.observe(el) : el.classList.add('is-in')));
  }

  /* Hero: child names type themselves in */
  function initNames(root) {
    root.querySelectorAll('[data-kf-names]').forEach((section) => {
      if (!once(section, 'kfNamesReady')) return;
      const names = (section.dataset.kfNames || '').split(',').map((s) => s.trim()).filter(Boolean);
      const typed = section.querySelector('.kf-name');
      const mirrors = section.querySelectorAll('[data-kf-name]');
      if (!typed || names.length < 2 || reduce) return;
      let i = 0;
      (async () => {
        while (section.isConnected) {
          await sleep(2400);
          if (document.hidden) continue;
          while (typed.textContent.length) {
            typed.textContent = typed.textContent.slice(0, -1);
            await sleep(55);
          }
          i = (i + 1) % names.length;
          for (let n = 1; n <= names[i].length; n++) {
            typed.textContent = names[i].slice(0, n);
            await sleep(90);
          }
          mirrors.forEach((m) => {
            m.textContent = names[i];
            m.classList.remove('kf-pop');
            void m.offsetWidth;
            m.classList.add('kf-pop');
          });
        }
      })();
    });
  }

  /* Hero: stickers drift with the mouse */
  function initParallax(root) {
    if (reduce || !window.matchMedia('(pointer: fine)').matches) return;
    root.querySelectorAll('[data-kf-parallax]').forEach((section) => {
      if (!once(section, 'kfParallaxReady')) return;
      const items = [...section.querySelectorAll('[data-depth]')];
      let frame = 0;
      let x = 0;
      let y = 0;
      section.addEventListener('pointermove', (event) => {
        const rect = section.getBoundingClientRect();
        x = (event.clientX - rect.left) / rect.width - 0.5;
        y = (event.clientY - rect.top) / rect.height - 0.5;
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          items.forEach((el) => {
            const depth = parseFloat(el.dataset.depth) || 0.3;
            el.style.translate = `${(-x * 44 * depth).toFixed(1)}px ${(-y * 44 * depth).toFixed(1)}px`;
          });
        });
      });
      section.addEventListener('pointerleave', () => items.forEach((el) => { el.style.translate = ''; }));
    });
  }

  /* Gentle snowfall on sections with data-kf-snow (pauses when off screen or tab hidden) */
  function initSnow(root) {
    if (reduce) return;
    root.querySelectorAll('[data-kf-snow]').forEach((section) => {
      if (!once(section, 'kfSnowReady')) return;
      const canvas = document.createElement('canvas');
      canvas.className = 'kf-snow';
      canvas.setAttribute('aria-hidden', 'true');
      section.prepend(canvas);
      const ctx = canvas.getContext('2d');
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      let w = 0;
      let h = 0;
      let flakes = [];
      let visible = false;
      let raf = 0;
      const make = (anywhere) => ({
        x: Math.random() * w,
        y: anywhere ? Math.random() * h : -8,
        r: 1.2 + Math.random() * 3.2,
        v: 0.35 + Math.random() * 0.9,
        p: Math.random() * 6.28,
        o: 0.45 + Math.random() * 0.5,
      });
      const resize = () => {
        w = section.clientWidth;
        h = section.clientHeight;
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        flakes = Array.from({ length: Math.round(Math.min(110, w / 12)) }, () => make(true));
      };
      const draw = () => {
        raf = 0;
        if (!visible || document.hidden) return;
        ctx.clearRect(0, 0, w, h);
        ctx.fillStyle = '#ffffff';
        flakes.forEach((f) => {
          f.y += f.v;
          f.p += 0.012;
          f.x += Math.sin(f.p) * 0.45;
          if (f.y > h + 8) Object.assign(f, make(false));
          ctx.globalAlpha = f.o;
          ctx.beginPath();
          ctx.arc(f.x, f.y, f.r, 0, 6.2832);
          ctx.fill();
        });
        ctx.globalAlpha = 1;
        raf = requestAnimationFrame(draw);
      };
      const start = () => {
        if (!raf && visible && !document.hidden) raf = requestAnimationFrame(draw);
      };
      new ResizeObserver(resize).observe(section);
      resize();
      new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        start();
      }).observe(section);
      document.addEventListener('visibilitychange', start);
    });
  }

  /* Live countdown (data-kf-countdown="MM-DD"), rolls over to next year after the day */
  function initCountdown(root) {
    root.querySelectorAll('[data-kf-countdown]').forEach((section) => {
      if (!once(section, 'kfCountdownReady')) return;
      const [mm, dd] = (section.dataset.kfCountdown || '12-25').split('-').map((n) => parseInt(n, 10));
      const month = (mm || 12) - 1;
      const day = dd || 25;
      const cells = ['d', 'h', 'm', 's'].map((k) => section.querySelector(`[data-kf-${k}]`));
      const target = () => {
        const now = new Date();
        let t = new Date(now.getFullYear(), month, day);
        if (now - t >= 86400000) t = new Date(now.getFullYear() + 1, month, day);
        return t;
      };
      const put = (el, value) => {
        if (!el) return;
        const text = String(value).padStart(2, '0');
        if (el.textContent === text) return;
        el.textContent = text;
        if (!reduce) {
          el.classList.remove('is-tick');
          void el.offsetWidth;
          el.classList.add('is-tick');
        }
      };
      const tick = () => {
        let ms = Math.max(0, target() - Date.now());
        const d = Math.floor(ms / 86400000);
        ms -= d * 86400000;
        const h = Math.floor(ms / 3600000);
        ms -= h * 3600000;
        const m = Math.floor(ms / 60000);
        ms -= m * 60000;
        [d, h, m, Math.floor(ms / 1000)].forEach((v, idx) => put(cells[idx], v));
      };
      tick();
      setInterval(tick, 1000);
    });
  }

  /* Video: plays only while visible, pause button, no autoplay for reduced motion */
  function initVideo(root) {
    root.querySelectorAll('[data-kf-video]').forEach((box) => {
      if (!once(box, 'kfVideoReady')) return;
      const video = box.querySelector('video');
      const button = box.querySelector('[data-kf-video-toggle]');
      if (!video) {
        if (button) button.hidden = true;
        return;
      }
      video.muted = true;
      video.playsInline = true;
      let userPaused = reduce;
      if (reduce) {
        video.removeAttribute('autoplay');
        video.pause();
      }
      const sync = () => {
        if (!button) return;
        button.setAttribute('aria-pressed', String(video.paused));
        button.setAttribute('aria-label', video.paused ? button.dataset.playLabel : button.dataset.pauseLabel);
      };
      new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !userPaused) video.play().catch(() => {});
        else video.pause();
      }, { threshold: 0.2 }).observe(box);
      if (button) {
        button.addEventListener('click', () => {
          if (video.paused) {
            userPaused = false;
            video.play().catch(() => {});
          } else {
            userPaused = true;
            video.pause();
          }
        });
      }
      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);
      sync();
    });
  }

  /* Confetti burst from a point */
  function confetti(x, y) {
    if (reduce) return;
    const colors = ['#ff6b6b', '#ffd23f', '#4cc9f0', '#06d6a0', '#b388eb', '#ff8fab'];
    const canvas = document.createElement('canvas');
    canvas.className = 'kf-confetti';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = window.innerWidth;
    const H = window.innerHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    const bits = Array.from({ length: 80 }, () => ({
      x, y,
      vx: (Math.random() - 0.5) * 13,
      vy: -4 - Math.random() * 11,
      a: Math.random() * 6.28,
      va: (Math.random() - 0.5) * 0.35,
      w: 6 + Math.random() * 7,
      h: 4 + Math.random() * 6,
      c: colors[(Math.random() * colors.length) | 0],
    }));
    let frames = 0;
    (function draw() {
      ctx.clearRect(0, 0, W, H);
      bits.forEach((b) => {
        b.vy += 0.33;
        b.vx *= 0.99;
        b.x += b.vx;
        b.y += b.vy;
        b.a += b.va;
        ctx.save();
        ctx.translate(b.x, b.y);
        ctx.rotate(b.a);
        ctx.fillStyle = b.c;
        ctx.fillRect(-b.w / 2, -b.h / 2, b.w, b.h);
        ctx.restore();
      });
      if (++frames < 120) requestAnimationFrame(draw);
      else canvas.remove();
    })();
  }
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('form[action*="/cart/add"] [type="submit"], [data-kf-confetti]');
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    confetti(rect.left + rect.width / 2, rect.top + rect.height / 2);
  });

  /* Product personalization: live preview of the child's name */
  function initPersonalize(root) {
    root.querySelectorAll('[data-kf-personalize]').forEach((box) => {
      if (!once(box, 'kfPzReady')) return;
      const template = box.dataset.preview || '';
      const out = box.querySelector('[data-kf-preview-text]');
      const tokenInputs = [...box.querySelectorAll('[data-token]')];
      if (!out || !template) return;
      const render = () => {
        const text = template.replace(/\{(\w+)\}/g, (match, token) => {
          const input = tokenInputs.find((el) => el.dataset.token === token);
          return input ? (input.value.trim() || input.dataset.example || '') : '';
        });
        if (out.textContent === text) return;
        out.textContent = text;
        if (!reduce) {
          out.classList.remove('kf-pop');
          void out.offsetWidth;
          out.classList.add('kf-pop');
        }
      };
      tokenInputs.forEach((el) => {
        el.addEventListener('input', render);
        el.addEventListener('change', render);
      });
      render();
    });
  }

  /* Before add to cart: required fields must be filled; empty optional fields are left out of the order */
  /* Read the id attribute directly: Shopify product forms contain <input name="id">, which makes form.id return that input */
  const personalizeFor = (form) => {
    const id = form && form.getAttribute ? form.getAttribute('id') : null;
    return id ? document.querySelector(`[data-kf-personalize][data-form-id="${CSS.escape(id)}"]`) : null;
  };
  function checkPersonalization(form, event) {
    const box = personalizeFor(form);
    if (!box) return true;
    const fields = [...box.querySelectorAll('input, select, textarea')];
    const invalid = fields.find((field) => !field.checkValidity());
    if (invalid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      box.classList.remove('kf-pz--shake');
      void box.offsetWidth;
      box.classList.add('kf-pz--shake');
      invalid.reportValidity();
      return false;
    }
    fields
      .filter((field) => !field.required && !field.value.trim())
      .forEach((field) => {
        field.disabled = true;
        setTimeout(() => { field.disabled = false; }, 2000);
      });
    return true;
  }
  document.addEventListener('click', (event) => {
    const button = event.target.closest('button[type="submit"], input[type="submit"], button[name="add"]');
    if (!button) return;
    checkPersonalization(button.form || button.closest('form'), event);
  }, true);
  document.addEventListener('submit', (event) => checkPersonalization(event.target, event), true);

  function kfFormatSync() {
    const checked = document.querySelector('input[type="radio"][value*="Printed"]:checked, select option[value*="Printed"]:checked');
    const printed = !!checked;
    document.querySelectorAll('[data-kf-trust="print"]').forEach((el) => { el.hidden = !printed; });
    document.querySelectorAll('[data-kf-trust="print"]').forEach((el) => {
      const d = el.parentElement.querySelector('[data-kf-trust="digital"]');
      if (d) d.hidden = printed;
    });
  }
  document.addEventListener('change', (e) => { if (e.target.closest('variant-picker, .variant-picker, fieldset')) setTimeout(kfFormatSync, 50); });
  document.addEventListener('DOMContentLoaded', kfFormatSync);

  function initAll(root = document) {
    initPersonalize(root);
    initReveal(root);
    initNames(root);
    initParallax(root);
    initSnow(root);
    initCountdown(root);
    initVideo(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  window.addEventListener('load', () => {
    const success = document.querySelector('.kf-success');
    if (success) {
      const rect = success.getBoundingClientRect();
      confetti(rect.left + rect.width / 2, rect.top);
    }
  });
})();
